import { useState } from 'react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import {
  scanMoetDuplicates,
  mergeMoetDuplicates,
  AdminRestaurant,
  MoetDuplicateScanResult,
  MoetDuplicateGroup,
  MoetDuplicateVariant,
  MoetMergeResult,
} from '../../../services/admin/adminService';

/**
 * Super-admin Testing card for Moët Hennessy scan-parse duplicates.
 *
 * Moët invoices are frequently SCANNED (often a stack into one PDF), and one garbled digit in the
 * printed "Artikel" number mints a second product identity for the same bottle — the platform is
 * keyed on the article id. This card clusters those, shows how often each variant occurs plus the
 * source invoice PDFs to open, and rewrites the ones the admin marks as duplicates onto the chosen
 * base product's id + name.
 *
 * English-only, like the rest of the Testing page (super-admin internal, never client-facing).
 */

/** Stable identity for a scanned group, so applied groups can be dropped from the list. */
function groupKey(group: MoetDuplicateGroup): string {
  return `${group.restaurantId}|${group.variants.map(v => v.articleId).sort().join('|')}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-NL');
}

export function MoetDuplicatesCard({ restaurants }: { restaurants: AdminRestaurant[] }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [restaurantId, setRestaurantId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MoetDuplicateScanResult | null>(null);
  // Groups the admin judged as NOT duplicates. Session-only on purpose — nothing is persisted, so
  // a dismissed group reappears on the next scan.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const filteredRestaurants = restaurants.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.ownerEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleScan = async (scopedRestaurantId?: string) => {
    try {
      setLoading(true);
      setError(null);
      setDismissed(new Set());
      setResult(await scanMoetDuplicates(scopedRestaurantId));
    } catch (err: any) {
      setError(err.message || 'Failed to scan Moët duplicates');
    } finally {
      setLoading(false);
    }
  };

  const visibleGroups = (result?.groups ?? []).filter(g => !dismissed.has(groupKey(g)));

  return (
    <div className={cn(cardClass, 'p-6')}>
      <div className="mb-4">
        <h3 className={cn(primaryText, semiBoldText)}>Moët Duplicates (scan-parse)</h3>
        <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
          Finds Moët Hennessy products split across several article ids by a mis-read scan — same volume,
          same unit price, near-identical name and id. Open the source invoices to see which parse is right,
          pick the base product, then apply to rewrite the duplicates' article id + name everywhere.
        </p>
      </div>

      <div className="mb-4">
        <label className={cn(secondaryText, 'text-xs block mb-1')}>Restaurant</label>
        <input
          type="text"
          placeholder="Search restaurants..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={cn(
            'w-full px-3 py-2 rounded-lg border mb-2',
            'border-gray-300 dark:border-gray-600',
            'bg-white dark:bg-gray-700',
            primaryText, smallTextSize
          )}
        />
        <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg mb-3">
          {filteredRestaurants.map(r => (
            <button
              key={r.id}
              onClick={() => setRestaurantId(r.id)}
              className={cn(
                'w-full text-left px-3 py-2 transition-colors',
                smallTextSize,
                restaurantId === r.id
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              )}
            >
              <div className={cn(primaryText, semiBoldText)}>{r.name}</div>
              <div className={cn(secondaryText, 'text-xs')}>{r.ownerEmail}</div>
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => handleScan(restaurantId)}
            disabled={!restaurantId || loading}
            className={cn(
              'px-4 py-2 rounded-lg transition-colors',
              'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              smallTextSize, semiBoldText
            )}
          >
            {loading ? 'Scanning...' : 'Scan Moët Duplicates'}
          </button>
          <button
            onClick={() => handleScan()}
            disabled={loading}
            title="Scan every restaurant with Moët orders"
            className={cn(
              'px-4 py-2 rounded-lg transition-colors',
              'bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 text-white dark:text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              smallTextSize, semiBoldText
            )}
          >
            {loading ? 'Scanning...' : 'Scan All Restaurants'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm mb-4">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-6">
          <div className={cn(secondaryText, smallTextSize, 'mb-3')}>
            <span className={cn(semiBoldText, visibleGroups.length > 0 ? 'text-orange-600 dark:text-orange-400' : '')}>
              {visibleGroups.length}
            </span>{' '}
            duplicate group(s) · {result.totalProductsScanned} Moët product(s) across {result.restaurantsScanned} restaurant(s)
          </div>

          {visibleGroups.length === 0 ? (
            <div className={cn(secondaryText, smallTextSize, 'py-4 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded-lg')}>
              No duplicate groups found.
            </div>
          ) : (
            <div className="space-y-3">
              {visibleGroups.map(group => (
                <MoetDuplicateGroupRow
                  key={groupKey(group)}
                  group={group}
                  onDone={() => setDismissed(prev => new Set(prev).add(groupKey(group)))}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MoetDuplicateGroupRow({ group, onDone }: { group: MoetDuplicateGroup; onDone: () => void }) {
  // Default base = the most-seen variant (sorted first by the server): the likeliest correct parse.
  const [baseArticleId, setBaseArticleId] = useState(group.variants[0]?.articleId ?? '');
  // Variants to rewrite — all non-base by default; unchecking leaves one untouched.
  const [included, setIncluded] = useState<Set<string>>(new Set(group.variants.map(v => v.articleId)));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Kept on screen after a successful apply so the admin sees what moved — and, when the same
  // product now sits on two ingredient rows, what still needs a manual fix.
  const [applied, setApplied] = useState<MoetMergeResult | null>(null);

  const baseVariant = group.variants.find(v => v.articleId === baseArticleId);
  const duplicateIds = group.variants
    .map(v => v.articleId)
    .filter(id => included.has(id) && id !== baseArticleId);

  const toggleIncluded = (articleId: string) => {
    setIncluded(prev => {
      const next = new Set(prev);
      if (next.has(articleId)) next.delete(articleId);
      else next.add(articleId);
      // Keep the base choice valid when it is excluded.
      if (!next.has(baseArticleId)) {
        setBaseArticleId(group.variants.find(v => next.has(v.articleId))?.articleId ?? '');
      }
      return next;
    });
  };

  const toggleExpanded = (articleId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(articleId)) next.delete(articleId);
      else next.add(articleId);
      return next;
    });
  };

  const handleApply = async () => {
    if (!baseVariant || duplicateIds.length === 0) return;
    if (!window.confirm(
      `Rewrite ${duplicateIds.length} article id(s) into ${baseVariant.articleId} — "${baseVariant.productName}"?\n\n` +
      `This rewrites order history, facturen, cost price, subrecipes, inventory keys, counts, quantity overrides, price alerts and waste links for ${group.restaurantName}. There is no undo.`
    )) return;
    try {
      setBusy(true);
      setError(null);
      setApplied(await mergeMoetDuplicates(group.restaurantId, baseVariant.articleId, baseVariant.productName, duplicateIds));
    } catch (err: any) {
      setError(err.message || 'Failed to merge duplicates');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className={cn(primaryText, smallTextSize)}>
          <span className={semiBoldText}>{group.restaurantName}</span>
          <span className={cn(secondaryText, 'mx-2')}>·</span>
          <span className="font-mono">{group.platform}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onDone}
            disabled={busy}
            title={applied ? 'Hide this group' : 'Not duplicates — hide until the next scan (nothing is stored)'}
            className={cn(
              'px-3 py-1.5 rounded-lg transition-colors',
              'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              primaryText, smallTextSize
            )}
          >
            {applied ? 'Close' : 'Not duplicates'}
          </button>
          {!applied && (
            <button
              onClick={handleApply}
              disabled={busy || duplicateIds.length === 0}
              className={cn(
                'px-3 py-1.5 rounded-lg transition-colors',
                'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                smallTextSize, semiBoldText
              )}
            >
              {busy ? 'Applying...' : `Apply${duplicateIds.length > 0 ? ` (${duplicateIds.length})` : ''}`}
            </button>
          )}
        </div>
      </div>

      {applied && <MergeSummary result={applied} />}

      {!applied && (
        <>
          <div className="space-y-1">
            {group.variants.map(variant => (
              <MoetVariantRow
                key={variant.articleId}
                variant={variant}
                isBase={baseArticleId === variant.articleId}
                isIncluded={included.has(variant.articleId)}
                isExpanded={expanded.has(variant.articleId)}
                groupId={groupKey(group)}
                onSelectBase={() => setBaseArticleId(variant.articleId)}
                onToggleIncluded={() => toggleIncluded(variant.articleId)}
                onToggleExpanded={() => toggleExpanded(variant.articleId)}
              />
            ))}
          </div>

          <p className={cn(secondaryText, 'text-xs mt-2')}>
            Radio = base product (its article id + name survive); checkbox = rewrite this one into the base.
            Click a row to see the invoices it came from.
          </p>
        </>
      )}

      {error && (
        <div className="mt-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
          {error}
        </div>
      )}
    </div>
  );
}

/** What the apply actually moved, plus anything left needing a manual fix. */
function MergeSummary({ result }: { result: MoetMergeResult }) {
  const moved = [
    [result.ordersUpdated, 'order(s)'],
    [result.factuurOrdersUpdated, 'factuur order(s)'],
    [result.costPriceUpdated, 'cost price analysis/analyses'],
    [result.subrecipeIngredientsUpdated, 'subrecipe ingredient(s)'],
    [result.inventoryKeysUpdated, 'inventory key(s)'],
    [result.inventoryCountsUpdated, 'count(s)'],
    [result.quantityOverridesUpdated, 'quantity override(s)'],
    [result.priceAlertsUpdated, 'price alert(s)'],
    [result.wasteUpdated, 'waste entr(y/ies)'],
    [result.alternativesRemoved, 'redundant alternative(s) removed'],
  ] as const;

  return (
    <div className="space-y-2">
      <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-xs">
        <span className={semiBoldText}>Applied.</span>{' '}
        {moved.filter(([count]) => count > 0).map(([count, label]) => `${count} ${label}`).join(' · ') || 'Nothing needed rewriting.'}
      </div>
      {result.duplicateIngredientRows.length > 0 && (
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-xs">
          <span className={semiBoldText}>Needs a manual look:</span> the base product now sits on TWO ingredient
          rows in {result.duplicateIngredientRows.join(', ')}. Both mis-parsed ids were added separately —
          merging the rows would change the recipe quantities, so remove the redundant one by hand.
        </div>
      )}
    </div>
  );
}

function MoetVariantRow({
  variant, isBase, isIncluded, isExpanded, groupId,
  onSelectBase, onToggleIncluded, onToggleExpanded,
}: {
  variant: MoetDuplicateVariant;
  isBase: boolean;
  isIncluded: boolean;
  isExpanded: boolean;
  groupId: string;
  onSelectBase: () => void;
  onToggleIncluded: () => void;
  onToggleExpanded: () => void;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border transition-colors',
        !isIncluded
          ? 'opacity-50 border-transparent'
          : isBase
            ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
            : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50'
      )}
    >
      <div className="flex items-center gap-3 px-3 py-2">
        <input
          type="checkbox"
          checked={isIncluded}
          onChange={onToggleIncluded}
          className="accent-purple-600"
          title="Include in the rewrite"
        />
        <input
          type="radio"
          name={`moet-base-${groupId}`}
          checked={isBase}
          disabled={!isIncluded}
          onChange={onSelectBase}
          className="accent-purple-600"
          title="Base product (survives)"
        />
        <button onClick={onToggleExpanded} className="flex-1 min-w-0 text-left">
          <div className={cn(primaryText, smallTextSize, semiBoldText, 'truncate')} title={variant.productName}>
            {variant.productName}
          </div>
          <div className={cn(secondaryText, 'text-xs font-mono')}>
            {variant.articleId}
            {variant.stdAmount != null && (
              <>
                <span className="mx-1">·</span>
                {variant.stdAmount}{variant.stdUnit ?? ''}
              </>
            )}
          </div>
        </button>
        <span className={cn(secondaryText, 'text-xs font-mono whitespace-nowrap text-right')}>
          <span className={cn(primaryText, semiBoldText)}>{variant.lineCount}x</span>
          <span className="mx-1">·</span>
          {variant.orderCount} invoice{variant.orderCount !== 1 ? 's' : ''}
          <span className="mx-1">·</span>
          {variant.latestPrice != null ? `€${variant.latestPrice.toFixed(2)}` : '—'}
          <span className="mx-1">·</span>
          {formatDate(variant.firstOrderDate)}–{formatDate(variant.lastOrderDate)}
        </span>
      </div>

      {isExpanded && (
        <div className="px-3 pb-3 pt-1 space-y-1">
          {variant.sources.map(source => (
            <div key={source.orderId} className="flex items-center gap-2 text-xs">
              <span className={cn(secondaryText, 'font-mono whitespace-nowrap')}>
                {formatDate(source.orderDate)}
              </span>
              <span className={cn(secondaryText, 'font-mono whitespace-nowrap')}>#{source.orderNumber}</span>
              <span className={cn(primaryText, 'flex-1 truncate')} title={source.productName}>
                {source.productName}
              </span>
              <span className={cn(secondaryText, 'font-mono whitespace-nowrap')}>
                {source.quantity}x {source.price != null ? `€${source.price.toFixed(2)}` : '—'}
              </span>
              {source.invoicesInFile > 1 && (
                <span
                  className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 whitespace-nowrap"
                  title={`This PDF holds ${source.invoicesInFile} invoices — a stacked scan`}
                >
                  stack ×{source.invoicesInFile}
                </span>
              )}
              {source.fileUrl ? (
                <a
                  href={source.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-textBlue-dark hover:underline whitespace-nowrap"
                >
                  Open file
                </a>
              ) : (
                <span className={cn(secondaryText, 'whitespace-nowrap')}>no file</span>
              )}
            </div>
          ))}
          {variant.sourcesTruncated && (
            <div className={cn(secondaryText, 'text-xs italic')}>
              Showing the {variant.sources.length} most recent of {variant.orderCount} invoices.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
